import { useEffect, useState } from "react";
import { ClipboardCheck, Loader2, Radio, StickyNote, UserRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { NUM } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { receiptBelgeNo } from "@/lib/receipt";

const rowsOf = (sale) => (sale?.items || []).map((it) => ({
  name: it.name, code: it.code || "", brand: it.brand || "", unit: it.unit || "ADET", product_id: it.product_id || null,
  quantity: Math.max(0, (Number(it.quantity) || 0) - (Number(it.returned) || 0)), description: "", on: true,
})).filter((r) => r.quantity > 0);

function PrepItemRow({ r, i, onChange }) {
  return (
    <div className={cn("rounded-lg border px-2.5 py-2 space-y-1.5 transition-opacity", r.on ? "bg-card border-border" : "bg-muted/40 border-dashed opacity-60")} data-testid={`prep-send-item-${i}`}>
      <div className="flex items-center gap-2">
        <Checkbox checked={r.on} onCheckedChange={(v) => onChange(i, { on: !!v })} data-testid={`prep-send-item-check-${i}`} />
        <div className="min-w-0 flex-1 text-[13px] font-medium leading-snug break-words">{r.name}{r.code && <span className="ml-1 text-xs font-mono text-muted-foreground">({r.code})</span>}</div>
        <span className="shrink-0 tnum text-xs font-bold text-foreground">{NUM(r.quantity)} {r.unit}</span>
      </div>
      {r.on && (
        <div className="relative">
          <StickyNote className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-amber-500" />
          <Input className="h-8 text-xs pl-8" placeholder="Ürüne özel not (opsiyonel) — ör. sarı-yeşil, 2. kat, acele" value={r.description}
            onChange={(e) => onChange(i, { description: e.target.value })} data-testid={`prep-send-item-note-${i}`} />
        </div>
      )}
    </div>
  );
}

// Hızlı Satış fişi VEYA açık sepet (canlı) → Hazırlanacak Listeler (genel not + ürüne özel not + personel seçimi)
export function SendToPrepDialog({ sale, cart, onClose, onSent }) {
  const src = sale || cart;
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [worker, setWorker] = useState("all");
  const [workers, setWorkers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!src) { return; }
    setRows(rowsOf(src)); setNote(sale ? sale.note || "" : ""); setWorker("all");
    api.get("/users").then((r) => setWorkers((r.data || []).filter((u) => u.role === "worker"))).catch(() => {});
  }, [src]); // eslint-disable-line

  const patch = (i, p) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const chosen = rows.filter((r) => r.on);

  const send = async () => {
    if (!chosen.length) return toast.error("En az bir ürün seçin");
    setBusy(true);
    if (cart) {
      try {
        await api.post(`/pos-carts/${cart.id}/prep`, {
          note: note.trim(), assigned_to: worker === "all" ? null : worker,
          items: chosen.map((r) => ({ product_id: r.product_id, description: r.description.trim() })),
          excluded: rows.filter((r) => !r.on).map((r) => r.product_id),
        });
        toast.success(`Canlı hazırlık listesine gönderildi · ${chosen.length} kalem — sepet değiştikçe liste güncellenir`);
        onSent?.();
      } catch (e) { toast.error(apiError(e)); }
      finally { setBusy(false); }
      return;
    }
    try {
      await api.post("/prep-lists", {
        title: sale.title || "Perakende Müşteri", label: receiptBelgeNo(sale), note: note.trim(),
        assigned_to: worker === "all" ? null : worker, quick_sale_id: sale.id,
        items: chosen.map(({ on, ...r }) => ({ ...r, description: r.description.trim() })),
      });
      toast.success(`${receiptBelgeNo(sale)} hazırlanacak listelere gönderildi · ${chosen.length} kalem`);
      onSent?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!src} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[96vw] sm:max-w-xl max-h-[92vh] overflow-hidden flex flex-col z-[60]" data-testid="prep-send-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" />Hazırlanacak Listeye Gönder{cart && <span className="inline-flex items-center gap-1 rounded-md bg-red-600 text-white text-[10px] font-black px-1.5 py-0.5 tracking-wider"><Radio className="h-3 w-3" />CANLI</span>}</DialogTitle>
        </DialogHeader>
        {src && (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            {cart ? (
              <p className="text-sm text-muted-foreground rounded-lg border border-red-200 bg-red-50/60 px-3 py-2" data-testid="prep-send-live-info"><b className="text-red-700">Canlı sepet</b> · {cart.title || "Perakende Müşteri"} — ödeme beklenmez. Sepette adet artırma/azaltma, ekleme ve çıkarma <b>listeye anında yansır</b>; depo değişikliği işaretli görür. Fiyatlar depoya gösterilmez.</p>
            ) : (
              <p className="text-sm text-muted-foreground"><b className="font-mono text-foreground">{receiptBelgeNo(sale)}</b> · {sale.title || "Perakende Müşteri"} — fiyatlar depoya gösterilmez, yalnız ürün ve miktarlar gider.</p>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Kime gönderilsin?</Label>
              <Select value={worker} onValueChange={setWorker}>
                <SelectTrigger data-testid="prep-send-worker"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Depo</SelectItem>
                  {workers.map((w) => <SelectItem key={w.id} value={w.id}><span className="flex items-center gap-1"><UserRound className="h-3 w-3" />{w.name}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><StickyNote className="h-3.5 w-3.5 text-amber-500" />Genel Not (tüm listeye)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="ör. Müşteri 17:00'de alacak, kapıya teslim" data-testid="prep-send-note" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ürünler · {chosen.length}/{rows.length}</Label>
                <button type="button" className="text-xs font-semibold text-primary" onClick={() => setRows((rs) => rs.map((r) => ({ ...r, on: !rs.every((x) => x.on) })))} data-testid="prep-send-toggle-all">
                  {rows.every((r) => r.on) ? "Hiçbirini seçme" : "Tümünü seç"}
                </button>
              </div>
              {rows.map((r, i) => <PrepItemRow key={i} r={r} i={i} onChange={patch} />)}
              {!rows.length && <div className="text-sm text-muted-foreground py-4 text-center">{cart ? "Sepette gönderilecek ürün yok." : "Fişte gönderilecek ürün yok (tamamı iade edilmiş)."}</div>}
              {cart && rows.some((r) => !r.on) && <p className="text-[11px] text-muted-foreground">Seçilmeyen ürünler bu listeye hiç gönderilmez (adetleri değişse de). Sepete sonradan eklenen ürünler otomatik eklenir.</p>}
            </div>
          </div>
        )}
        <DialogFooter className="pt-3">
          <Button variant="outline" onClick={onClose} data-testid="prep-send-cancel">Vazgeç</Button>
          <Button onClick={send} disabled={busy || !chosen.length} data-testid="prep-send-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}Gönder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
